import { cn } from '@/lib/utils';
import OrderStatusBadge from './OrderStatusBadge';
import { useOrderFormContext } from '../PublicEventOrderForm/OrderFormContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import i18n from '@/utils/i18n';
import { ReactNode } from 'react';
import { MerchantOrderStatusEnum } from '@/graphql/generatedTypes';
import OrderStatusSelect from './OrderStatusSelect';
import { Routes } from '@/utils/routes';
import { EVENT_ORDER_SETTABLE_STATUSES } from './orderStatusConfig';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { isDev, isLocalDev } from '@/utils/isDev';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import SvgIcon from '@/components/Icon/SvgIcon';

type OrderInfoProps = {
  className?: string;
  isLoading: boolean;
  children: ReactNode;
};

export function OrderInfo({ className, isLoading, children }: OrderInfoProps) {
  return (
    <Skeleton showSpinner loading={isLoading}>
      <Card
        className={cn(
          'tw:p-6 tw:w-full tw:flex tw:flex-col tw:gap-4',
          className,
        )}
      >
        {children}
      </Card>
    </Skeleton>
  );
}

OrderInfo.Title = function OrderInfoTitle() {
  return (
    <h2 className="tw:text-2xl tw:font-semibold tw:leading-[1.2] tw:text-primary tw:tracking-[-0.48px]">
      {i18n.t('EventOrder:orderInfo')}
    </h2>
  );
};

OrderInfo.OrderStatus = function OrderInfoOrderStatus({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="tw:flex tw:gap-2 tw:items-start tw:w-full">
      <p className="tw:font-medium tw:text-primary tw:w-[10rem]">
        {i18n.t('EventOrder:orderStatus')}:
      </p>
      {children}
    </div>
  );
};

OrderInfo.FormDeadline = function OrderInfoFormDeadline({
  deadline,
  children,
}: {
  deadline?: string;
  children?: ReactNode;
}) {
  return (
    <div className="tw:flex tw:gap-2 tw:items-start tw:w-full">
      <p className="tw:font-medium tw:text-primary tw:w-[10rem]">
        {i18n.t('EventOrder:formDeadline')}:
      </p>
      {children || (
        <p className="tw:flex-1 tw:font-normal tw:text-primary">
          {deadline ?? '-'}
        </p>
      )}
    </div>
  );
};

OrderInfo.Event = function OrderInfoEvent({ name }: { name?: string | null }) {
  return (
    <div className="tw:flex tw:gap-2 tw:items-start tw:w-full">
      <p className="tw:font-medium tw:text-primary tw:w-[10rem]">
        {i18n.t('EventOrder:event')}:
      </p>
      <p className="tw:flex-1 tw:font-normal tw:text-primary">{name}</p>
    </div>
  );
};

OrderInfo.EventAccount = function OrderInfoEventAccount({
  name,
}: {
  name?: string | null;
}) {
  return (
    <div className="tw:flex tw:gap-2 tw:items-start tw:w-full">
      <p className="tw:font-medium tw:text-primary tw:w-[10rem]">
        {i18n.t('EventOrder:eventAccount')}:
      </p>
      <p className="tw:flex-1 tw:font-normal tw:text-primary">{name}</p>
    </div>
  );
};

OrderInfo.EventDates = function OrderInfoEventDates({
  dates,
}: {
  dates?: string;
}) {
  return (
    <div className="tw:flex tw:gap-2 tw:items-start tw:w-full">
      <p className="tw:font-medium tw:text-primary tw:w-[10rem]">
        {i18n.t('EventOrder:eventDates')}:
      </p>
      <p className="tw:flex-1 tw:font-normal tw:text-primary">{dates}</p>
    </div>
  );
};

OrderInfo.EventManager = function OrderInfoEventManager({
  manager,
}: {
  manager?: {
    name?: string | null;
    phone?: string | null;
    mobile?: string | null;
  };
}) {
  return (
    <div className="tw:flex tw:gap-2 tw:items-start tw:w-full">
      <p className="tw:font-medium tw:text-primary tw:w-[10rem]">
        {i18n.t('EventOrder:eventManager')}:
      </p>
      <div className="tw:flex tw:flex-1 tw:flex-col tw:items-start tw:font-normal tw:text-primary">
        <p>{manager?.name ?? '-'}</p>
        {manager?.phone && <p>{manager.phone}</p>}
        {!manager?.phone && manager?.mobile && <p>{manager.mobile}</p>}
      </div>
    </div>
  );
};

OrderInfo.Connected = function OrderInfoConnected({
  className,
}: {
  className?: string;
}) {
  const { formLoadingStatus, fetchedFormInfo } = useOrderFormContext();

  const isLoading =
    formLoadingStatus === 'initializing' || formLoadingStatus === 'loading';

  const {
    event,
    event_account: eventAccount,
    ops_order: opsOrder,
  } = fetchedFormInfo || {};
  const { event_manager: manager } = event || {};

  // Compute derived values
  const remoteOrderStatus = opsOrder?.status;
  const eventName = event?.name;

  const formDeadlineDate = event?.merchant_order_form_deadline
    ? new Date(event.merchant_order_form_deadline)
    : undefined;

  const formDeadline = formDeadlineDate?.toLocaleDateString();

  const isFormExpired = formDeadlineDate
    ? formDeadlineDate < new Date()
    : false;

  const eventDates =
    event?.start_date && event?.end_date
      ? `${new Date(event.start_date).toLocaleDateString()} - ${new Date(event.end_date).toLocaleDateString()}`
      : undefined;

  const eventManager = manager
    ? {
        name: manager.full_name,
        phone: manager.phone,
        mobile: manager.mobile,
      }
    : undefined;

  return (
    <OrderInfo className={className} isLoading={isLoading}>
      <OrderInfo.Title />
      <OrderInfo.OrderStatus>
        <OrderStatusBadge expired={isFormExpired} status={remoteOrderStatus} />
      </OrderInfo.OrderStatus>
      <OrderInfo.FormDeadline deadline={formDeadline} />
      <Separator />
      <div className="tw:flex tw:flex-col tw:gap-2 tw:w-full">
        <OrderInfo.Event name={eventName} />
        <OrderInfo.EventAccount name={eventAccount?.name} />
        <OrderInfo.EventDates dates={eventDates} />
        <OrderInfo.EventManager manager={eventManager} />
      </div>
    </OrderInfo>
  );
};

OrderInfo.Admin = function OrderInfoAdmin({
  className,
  isLoading,
  orderStatus,
  hasOpsOrder,
  formDeadline,
  eventName,
  eventAccountName,
  eventAccountEmail,
  eventDates,
  eventManager,
  eventId,
  eventAccountId,
  salesOrderUrl,
  salesOrderZohoId,
  salesOrderSendReferenceId,
  canSendSalesOrder,
  onSendSalesOrder,
  isSubmitting,
  onOrderStatusChange,
  disabledStatuses,
}: {
  className?: string;
  isLoading: boolean;
  orderStatus: MerchantOrderStatusEnum | null;
  hasOpsOrder: boolean;
  formDeadline?: string;
  eventName?: string | null;
  eventAccountName?: string | null;
  eventAccountEmail?: string | null;
  eventDates?: string;
  eventManager?: {
    name?: string | null;
    phone?: string | null;
    mobile?: string | null;
  };
  eventId?: string | null;
  eventAccountId?: string | null;
  salesOrderUrl: string | null;
  salesOrderZohoId: string | null;
  salesOrderSendReferenceId?: string | null;
  canSendSalesOrder?: boolean;
  onSendSalesOrder?: () => Promise<boolean>;
  isSubmitting?: boolean;
  onOrderStatusChange?: (status: MerchantOrderStatusEnum) => void;
  disabledStatuses?: MerchantOrderStatusEnum[];
}) {
  const isFormExpired = formDeadline
    ? new Date(formDeadline) < new Date()
    : false;

  return (
    <OrderInfo className={className} isLoading={isLoading}>
      <OrderInfo.Title />
      <OrderInfoRow label={i18n.t('EventOrder:orderStatus')}>
        {hasOpsOrder && onOrderStatusChange ? (
          <OrderStatusSelect
            value={orderStatus}
            onChange={onOrderStatusChange}
            disabled={isSubmitting}
            variant="admin"
            allowedStatuses={EVENT_ORDER_SETTABLE_STATUSES}
            disabledStatuses={disabledStatuses}
          />
        ) : (
          <OrderStatusBadge
            expired={isFormExpired}
            status={orderStatus ?? undefined}
            variant="admin"
          />
        )}
      </OrderInfoRow>
      <OrderInfoRow label={i18n.t('EventOrder:deadline') || 'Deadline'}>
        <p className="tw:font-normal tw:text-primary">{formDeadline ?? '-'}</p>
      </OrderInfoRow>
      <Separator />
      <div className="tw:flex tw:flex-col tw:gap-2 tw:w-full">
        <OrderInfoRow label={i18n.t('EventOrder:event')}>
          {eventId ? (
            <a
              href={Routes.eventDetail(eventId)}
              className="tw:font-normal tw:text-lilac-700 tw:dark:text-primary tw:underline tw:underline-offset-4 tw:hover:text-primary/80"
            >
              {eventName || eventId}
            </a>
          ) : (
            <p className="tw:font-normal tw:text-lilac-700 tw:dark:text-primary">
              {eventName}
            </p>
          )}
        </OrderInfoRow>
        <OrderInfoRow label={i18n.t('EventOrder:eventAccount')}>
          {eventAccountId ? (
            <a
              href={Routes.eventAccountDetail(eventAccountId)}
              className="tw:font-normal tw:text-lilac-700 tw:dark:text-primary tw:underline tw:underline-offset-4 tw:hover:text-primary/80"
            >
              {eventAccountName || eventAccountId}
            </a>
          ) : (
            <p className="tw:font-normal tw:text-lilac-700 tw:dark:text-primary">
              {eventAccountName}
            </p>
          )}
        </OrderInfoRow>
        <OrderInfo.EventDates dates={eventDates} />
        <OrderInfo.EventManager manager={eventManager || undefined} />
      </div>
      <Separator />
      <OrderInfoLinks
        salesOrderUrl={salesOrderUrl}
        salesOrderZohoId={salesOrderZohoId}
        salesOrderSendReferenceId={salesOrderSendReferenceId}
        canSendSalesOrder={canSendSalesOrder}
        onSendSalesOrder={onSendSalesOrder}
        isSubmitting={isSubmitting}
        hasOpsOrder={hasOpsOrder}
        eventAccountEmail={eventAccountEmail}
      />
    </OrderInfo>
  );
};

// Helper component for consistent row layout
function OrderInfoRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="tw:flex tw:gap-2 tw:items-center tw:w-full">
      <p className="tw:font-medium tw:text-primary tw:w-[10rem] tw:shrink-0">
        {label}:
      </p>
      <div className="tw:flex-1">{children}</div>
    </div>
  );
}

// Helper component for order links section
function OrderInfoLinks({
  salesOrderUrl,
  salesOrderZohoId,
  salesOrderSendReferenceId,
  canSendSalesOrder,
  onSendSalesOrder,
  isSubmitting,
  hasOpsOrder,
  eventAccountEmail,
}: {
  salesOrderUrl: string | null;
  salesOrderZohoId: string | null;
  salesOrderSendReferenceId?: string | null;
  canSendSalesOrder?: boolean;
  onSendSalesOrder?: () => Promise<boolean>;
  isSubmitting?: boolean;
  hasOpsOrder: boolean;
  eventAccountEmail?: string | null;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleConfirmSend = async () => {
    if (onSendSalesOrder) {
      const success = await onSendSalesOrder();
      if (success) {
        setIsDialogOpen(false);
      }
    }
  };

  const isEmailMissing = !eventAccountEmail;
  const isButtonDisabled = isSubmitting || !canSendSalesOrder || isEmailMissing;

  // Determine Customer.io URL based on environment
  const getCustomerIoUrl = (referenceId: string): string => {
    if (isLocalDev() || isDev()) {
      return `https://fly.customer.io/workspaces/102587/journeys/outbox/deliveries/${referenceId}`;
    }
    return `https://fly.customer.io/workspaces/102586/journeys/outbox/deliveries/${referenceId}`;
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:w-full">
      <OrderInfoRow label={i18n.t('MerchantOrder:salesOrder') || 'Sales Order'}>
        {hasOpsOrder && canSendSalesOrder && (
          <Button
            size="sm"
            disabled={isButtonDisabled}
            className="tw:w-full tw:max-w-[190px]"
            onClick={() => setIsDialogOpen(true)}
          >
            <SvgIcon name="mail-letter-envelope" />
            {i18n.t('MerchantOrder:sendSalesOrder') || 'Send Sales Order'}
          </Button>
        )}
        {hasOpsOrder && !canSendSalesOrder && (
          <Button
            size="sm"
            disabled
            className="tw:w-full tw:max-w-[190px]"
            onClick={() => {}}
          >
            {i18n.t('MerchantOrder:sendSalesOrderHelp') ||
              'Approve order first'}
          </Button>
        )}
        {salesOrderUrl && (
          <>
            <br />
            <a
              href={salesOrderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tw:w-full tw:max-w-[190px] tw:mt-3"
            >
              <Button
                size="sm"
                variant="secondary"
                className="tw:w-full tw:max-w-[190px]"
              >
                {i18n.t('EventOrder:openSalesOrder') || 'Open sales order'}
                <SvgIcon name="arrow-box-open" />
              </Button>
            </a>
            <br />
          </>
        )}
      </OrderInfoRow>
      <OrderInfoRow label="Send Reference">
        {salesOrderSendReferenceId ? (
          <a
            href={getCustomerIoUrl(salesOrderSendReferenceId)}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:font-normal tw:text-lilac-700 tw:dark:text-primary tw:underline tw:underline-offset-4 tw:hover:text-primary/80"
          >
            Customer.io
          </a>
        ) : (
          <span className="tw:font-normal tw:text-muted-foreground">
            Not send yet
          </span>
        )}
      </OrderInfoRow>
      {hasOpsOrder && (
        <div className="tw:mt-2 tw:flex tw:flex-col tw:gap-1">
          <Button
            size="sm"
            disabled={isButtonDisabled}
            className="tw:w-full"
            onClick={() => setIsDialogOpen(true)}
          >
            {i18n.t('MerchantOrder:sendSalesOrder') || 'Send Sales Order'}
          </Button>
          {!canSendSalesOrder && (
            <p className="tw:text-xs tw:text-muted-foreground tw:text-center">
              {i18n.t('MerchantOrder:sendSalesOrderHelp') ||
                'Set status to "Approved" to send the sales order'}
            </p>
          )}

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {i18n.t('MerchantOrder:sendSalesOrderTitle') ||
                    'Send Sales Order?'}
                </DialogTitle>
                <DialogDescription className="tw:space-y-2">
                  {isEmailMissing ? (
                    <div className="tw:text-destructive tw:text-sm tw:font-medium">
                      {i18n.t('MerchantOrder:sendSalesOrderEmailRequired') ||
                        'Cannot send sales order: Event account email is not set. Please update the event account email in Salesforce.'}
                    </div>
                  ) : (
                    <div>
                      {i18n.t('MerchantOrder:sendSalesOrderDescription', {
                        email: eventAccountEmail,
                      }) ||
                        `Are you sure you want to send the sales order to ${eventAccountEmail}?`}
                    </div>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isSubmitting}
                >
                  {i18n.t('Common:cancel')}
                </Button>
                <Button onClick={handleConfirmSend} disabled={isButtonDisabled}>
                  {isSubmitting
                    ? i18n.t('Common:sending') || 'Sending...'
                    : isEmailMissing
                      ? i18n.t(
                          'MerchantOrder:sendSalesOrderEmailRequiredShort',
                        ) || 'Email Required'
                      : i18n.t('MerchantOrder:sendSalesOrder') ||
                        'Send Sales Order'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
